import { useMemo } from "react";
import { usePrices } from "@/lib/prices-context";
import { ASSET_DECIMALS } from "@/lib/api";

/**
 * Bloomberg-style ticker tape pinned to the viewport bottom. Pure decoration
 * but loud signal: the page is reading the live feed, right now. Continuous
 * scroll using CSS animation (no JS rAF) so it doesn't compete with anything.
 * Desktop only — too narrow on mobile to be useful.
 */
export function LatencyTape() {
  const { assets, lastRound } = usePrices();

  const tokens = useMemo(() => {
    if (!assets) return [];
    const list: string[] = [];
    for (const key in assets) {
      const a = assets[key];
      const dp = ASSET_DECIMALS[key] ?? 2;
      const price = a.price_human.toLocaleString("en-US", {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });
      list.push(`${a.tag} $${price}`);
    }
    return list;
  }, [assets]);

  if (tokens.length === 0) return null;

  // Duplicate the list so the CSS-only loop reads seamlessly.
  const doubled = [...tokens, ...tokens];

  return (
    <div
      aria-hidden
      className="hidden md:block fixed bottom-0 inset-x-0 z-30 border-t border-border bg-bg-deep/80 backdrop-blur-sm overflow-hidden"
    >
      <div className="flex items-center gap-6 px-4 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em]">
        <span className="shrink-0 font-medium text-emerald">
          ● R{lastRound != null ? lastRound.toLocaleString("en-US") : "—"}
        </span>
        <div className="relative flex-1 overflow-hidden">
          <div
            className="flex gap-8 whitespace-nowrap tabular text-m55"
            style={{
              animation: "tape-roll 80s linear infinite",
              width: "max-content",
            }}
          >
            {doubled.map((token, i) => (
              <span key={i} className="shrink-0">
                {token}
                <span className="text-m30 ml-8">·</span>
              </span>
            ))}
          </div>
        </div>
        <span className="shrink-0 font-medium text-m45">500MS · ED25519</span>
      </div>

      <style>{`
        @keyframes tape-roll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="tape-roll"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
